import { supabase } from '../../../lib/supabase';
import type { BatchStock, BatchStockFormData, StockSummary, BatchWithMedicine } from '../types';

export const stockService = {
  async getAll(): Promise<BatchStock[]> {
    const { data, error } = await supabase
      .from('batch_stock')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;

    return (data || []).map(batch => ({
      id: batch.id,
      medicineId: batch.medicine_id,
      batchNo: batch.batch_no,
      expiryDate: batch.expiry_date,
      quantityOnHand: batch.quantity_on_hand,
      location: batch.location || 'main',
      unitPrice: batch.unit_price,
      mrp: batch.mrp || 0,
      createdAt: batch.created_at,
      updatedAt: batch.updated_at,
    }));
  },

  async getById(id: string): Promise<BatchStock | null> {
    const { data, error } = await supabase
      .from('batch_stock')
      .select('*')
      .eq('id', id)
      .single();

    if (error) return null;
    if (!data) return null;

    return {
      id: data.id,
      medicineId: data.medicine_id,
      batchNo: data.batch_no,
      expiryDate: data.expiry_date,
      quantityOnHand: data.quantity_on_hand,
      location: data.location || 'main',
      unitPrice: data.unit_price,
      mrp: data.mrp || 0,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };
  },

  async getByMedicine(medicineId: string): Promise<BatchStock[]> {
    const { data, error } = await supabase
      .from('batch_stock')
      .select('*')
      .eq('medicine_id', medicineId)
      .order('expiry_date', { ascending: true });

    if (error) throw error;

    return (data || []).map(batch => ({
      id: batch.id,
      medicineId: batch.medicine_id,
      batchNo: batch.batch_no,
      expiryDate: batch.expiry_date,
      quantityOnHand: batch.quantity_on_hand,
      location: batch.location || 'main',
      unitPrice: batch.unit_price,
      mrp: batch.mrp || 0,
      createdAt: batch.created_at,
      updatedAt: batch.updated_at,
    }));
  },

  async upsert(data: BatchStockFormData): Promise<BatchStock> {
    const { data: batch, error } = await supabase
      .from('batch_stock')
      .upsert({
        medicine_id: data.medicineId,
        batch_no: data.batchNo,
        expiry_date: data.expiryDate,
        quantity_on_hand: data.quantityOnHand,
        location: data.location || 'main',
        unit_price: data.unitPrice,
        mrp: data.mrp || 0,
      }, {
        onConflict: 'medicine_id,batch_no',
      })
      .select()
      .single();

    if (error) throw error;

    return {
      id: batch.id,
      medicineId: batch.medicine_id,
      batchNo: batch.batch_no,
      expiryDate: batch.expiry_date,
      quantityOnHand: batch.quantity_on_hand,
      location: batch.location || 'main',
      unitPrice: batch.unit_price,
      mrp: batch.mrp || 0,
      createdAt: batch.created_at,
      updatedAt: batch.updated_at,
    };
  },

  async getSummary(): Promise<StockSummary[]> {
    const { data, error } = await supabase
      .from('batch_stock')
      .select(`
        medicine_id,
        quantity_on_hand,
        expiry_date,
        medicine:medicines(id, name, reorder_level)
      `);

    if (error) throw error;

    const medicineStocks = new Map<string, { batches: any[], medicine: any }>();
    
    (data || []).forEach((batch: any) => {
      if (!batch.medicine) return;
      
      if (!medicineStocks.has(batch.medicine_id)) {
        medicineStocks.set(batch.medicine_id, {
          batches: [],
          medicine: batch.medicine,
        });
      }
      medicineStocks.get(batch.medicine_id)!.batches.push(batch);
    });

    const summary: StockSummary[] = [];
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);

    medicineStocks.forEach((value, medicineId) => {
      const totalStock = value.batches.reduce((sum, batch) => sum + batch.quantity_on_hand, 0);
      const nearestExpiry = value.batches
        .filter(b => b.quantity_on_hand > 0)
        .sort((a, b) => new Date(a.expiry_date).getTime() - new Date(b.expiry_date).getTime())[0]?.expiry_date || '';

      summary.push({
        medicineId,
        medicineName: value.medicine.name,
        totalStock,
        nearestExpiry,
        reorderLevel: value.medicine.reorder_level || 10,
        isLowStock: totalStock <= (value.medicine.reorder_level || 10),
        isNearExpiry: nearestExpiry ? new Date(nearestExpiry) <= thirtyDaysFromNow : false,
      });
    });

    return summary;
  },

  async getBatchesWithMedicine(): Promise<BatchWithMedicine[]> {
    const { data, error } = await supabase
      .from('batch_stock')
      .select(`
        *,
        medicine:medicines(*)
      `)
      .order('created_at', { ascending: false });

    if (error) throw error;

    return (data || []).map(batch => ({
      id: batch.id,
      medicineId: batch.medicine_id,
      batchNo: batch.batch_no,
      expiryDate: batch.expiry_date,
      quantityOnHand: batch.quantity_on_hand,
      location: batch.location || 'main',
      unitPrice: batch.unit_price,
      mrp: batch.mrp || 0,
      createdAt: batch.created_at,
      updatedAt: batch.updated_at,
      medicine: {
        id: batch.medicine.id,
        name: batch.medicine.name,
        genericName: batch.medicine.generic_name,
        brandName: batch.medicine.brand_name,
        strength: batch.medicine.strength,
        form: batch.medicine.form,
        route: batch.medicine.route,
        manufacturer: batch.medicine.manufacturer,
        reorderLevel: batch.medicine.reorder_level || 10,
        status: batch.medicine.status,
        createdAt: batch.medicine.created_at,
        updatedAt: batch.medicine.updated_at,
      },
    }));
  },

  async getLowStock(): Promise<StockSummary[]> {
    const summary = await this.getSummary();
    return summary.filter(s => s.isLowStock);
  },

  async getNearExpiry(): Promise<StockSummary[]> {
    const summary = await this.getSummary();
    return summary.filter(s => s.isNearExpiry);
  },

  async getAvailableBatches(medicineId: string): Promise<BatchStock[]> {
    const now = new Date().toISOString().split('T')[0];
    
    const { data, error } = await supabase
      .from('batch_stock')
      .select('*')
      .eq('medicine_id', medicineId)
      .gt('quantity_on_hand', 0)
      .gt('expiry_date', now)
      .order('expiry_date', { ascending: true });

    if (error) throw error;

    return (data || []).map(batch => ({
      id: batch.id,
      medicineId: batch.medicine_id,
      batchNo: batch.batch_no,
      expiryDate: batch.expiry_date,
      quantityOnHand: batch.quantity_on_hand,
      location: batch.location || 'main',
      unitPrice: batch.unit_price,
      mrp: batch.mrp || 0,
      createdAt: batch.created_at,
      updatedAt: batch.updated_at,
    }));
  }
};
